<template>
    <div>
        <!-- Full-screen loading overlay -->
        <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
            <div class="text-center">
                <v-progress-circular
                    indeterminate
                    size="64"
                    color="primary"
                ></v-progress-circular>
                <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
            </div>
        </v-overlay>

        <v-row>
            <!-- Left Column - 4/12 -->
            <v-col cols="12" md="4">
                <!-- Total Profit Card -->
                <v-card class="mb-4 elevation-2">
                    <v-card-title class="text-h6 d-flex align-center">
                        <v-icon class="mr-2" color="success">mdi-currency-usd</v-icon>
                        Tổng lợi nhuận
                    </v-card-title>
                    <v-card-text>
                        <div class="text-h4 text-success font-weight-bold">
                            {{ formatCurrency(totalProfit) }}
                        </div>
                        <div class="text-caption text-grey">
                            Tổng lợi nhuận từ affiliate
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Top 5 Affiliates -->
                <v-card class="elevation-2 top-affiliate-card">
                    <v-card-title class="text-h6 d-flex align-center">
                        <v-icon class="mr-2" color="primary">mdi-trophy</v-icon>
                        Top 5 Cộng tác viên xuất sắc
                    </v-card-title>
                    <v-card-subtitle class="text-caption">
                        Xếp hạng theo lợi nhuận
                    </v-card-subtitle>
                    <v-card-text>
                        <v-list class="py-0">
                            <v-list-item
                                v-for="(affiliate, index) in topAffiliates"
                                :key="affiliate.id"
                                class="px-0 affiliate-item"
                            >
                                <template v-slot:prepend>
                                    <v-avatar 
                                        :color="getRankColor(index)" 
                                        size="36"
                                        class="mr-3"
                                    >
                                        <span class="text-white font-weight-bold">{{ index + 1 }}</span>
                                    </v-avatar>
                                </template>
                                <v-list-item-content class="affiliate-content">
                                    <v-list-item-title class="font-weight-medium affiliate-name">
                                        <v-icon size="16" class="mr-2">mdi-account-circle</v-icon>
                                        {{ affiliate.name }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle class="affiliate-email">
                                        <v-icon size="14" class="mr-2">mdi-email-outline</v-icon>
                                        {{ affiliate.email }}
                                    </v-list-item-subtitle>
                                    <v-list-item-subtitle class="text-success font-weight-bold affiliate-profit">
                                        <v-icon size="14" class="mr-2">mdi-currency-usd</v-icon>
                                        {{ formatCurrency(affiliate.total_profit) }}
                                    </v-list-item-subtitle>
                                    <v-list-item-subtitle class="text-info affiliate-orders">
                                        <v-icon size="14" class="mr-2">mdi-cart-outline</v-icon>
                                        {{ affiliate.total_orders }} đơn hàng
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Right Column - 8/12 -->
            <v-col cols="12" md="8">
                <v-card class="elevation-2">
                    <v-card-title class="text-h6 d-flex align-center justify-space-between">
                        <div class="d-flex align-center">
                            <v-icon class="mr-2">mdi-account-group</v-icon>
                            Đơn hàng Affiliate
                        </div>
                        <v-text-field
                            v-model="search"
                            label="Tìm kiếm đơn hàng"
                            @input="onSearchInput"
                            clearable
                            prepend-inner-icon="mdi-magnify"
                            variant="outlined"
                            density="compact"
                            class="search-input"
                            style="max-width: 320px;"
                        />
                    </v-card-title>
                                                          <v-card-text>
                        <!-- Desktop Table View -->
                        <div v-if="!$vuetify.display.mobile" class="desktop-view">
                            <div class="table-container">
                                <v-table class="elevation-1">
                                    <thead>
                                        <tr>
                                            <th @click="sort('product_names')" style="cursor:pointer; min-width: 150px;">
                                                Tên sản phẩm
                                                <v-icon :style="sortBy === 'product_names' ? '' : 'opacity:0.3'">
                                                    {{ sortBy === 'product_names' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                                                </v-icon>
                                            </th>
                                            <th @click="sort('price')" style="cursor:pointer; min-width: 120px;">
                                                Giá bán
                                                <v-icon :style="sortBy === 'price' ? '' : 'opacity:0.3'">
                                                    {{ sortBy === 'price' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                                                </v-icon>
                                            </th>
                                            <th @click="sort('shared')" style="cursor:pointer; min-width: 100px;">
                                                % Chia sẻ
                                                <v-icon :style="sortBy === 'shared' ? '' : 'opacity:0.3'">
                                                    {{ sortBy === 'shared' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                                                </v-icon>
                                            </th>
                                            <th @click="sort('profit')" style="cursor:pointer; min-width: 120px;">
                                                Lợi nhuận
                                                <v-icon :style="sortBy === 'profit' ? '' : 'opacity:0.3'">
                                                    {{ sortBy === 'profit' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                                                </v-icon>
                                            </th>
                                            <th style="min-width: 180px;">
                                                Cộng tác viên
                                            </th>
                                            <th @click="sort('created_at')" style="cursor:pointer; min-width: 130px;">
                                                Ngày tạo
                                                <v-icon :style="sortBy === 'created_at' ? '' : 'opacity:0.3'">
                                                    {{ sortBy === 'created_at' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                                                </v-icon>
                                            </th>
                                            <th @click="sort('status')" style="cursor:pointer; min-width: 120px;">
                                                Trạng thái
                                                <v-icon :style="sortBy === 'status' ? '' : 'opacity:0.3'">
                                                    {{ sortBy === 'status' ? (sortOrder === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down') : 'mdi-arrow-up' }}
                                                </v-icon>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="order in allAffiliateOrders" :key="order.id">
                                            <td>
                                                <div v-if="order.product_names && order.product_names.length > 0">
                                                    {{ order.product_names.join(', ') }}
                                                </div>
                                                <div v-else-if="order.product_name">
                                                    {{ order.product_name }}
                                                </div>
                                                <div v-else class="text-grey">
                                                    Chưa có sản phẩm
                                                </div>
                                            </td>
                                            <td>{{ formatCurrency(order.price) }}</td>
                                            <td>
                                                <v-chip color="info" size="small">
                                                    {{ order.shared }}%
                                                </v-chip>
                                            </td>
                                            <td class="text-success font-weight-bold">
                                                {{ formatCurrency(order.profit) }}
                                            </td>
                                            <td>
                                                <div class="seller-info">
                                                    <div class="seller-row">
                                                        <v-icon size="14" class="mr-1">mdi-account</v-icon>
                                                        <span class="text-caption">{{ order.seller_name }}</span>
                                                    </div>
                                                    <div class="seller-row">
                                                        <v-icon size="14" class="mr-1">mdi-email</v-icon>
                                                        <span class="text-caption text-grey-darken-1">{{ order.seller_email }}</span>
                                                    </div>
                                                    <div class="seller-row">
                                                        <v-icon size="14" class="mr-1">mdi-phone</v-icon>
                                                        <span class="text-caption text-grey-darken-1">{{ order.seller_phone }}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{{ formatDate(order.created_at) }}</td>
                                            <td>
                                                <v-chip :color="getStatusColor(order.status)" size="small">
                                                    {{ getStatusText(order.status) }}
                                                </v-chip>
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-table>
                                <v-pagination
                                    v-model="page"
                                    :length="totalPages"
                                    @update:modelValue="onPageChange"
                                    class="custom-pagination"
                                />
                            </div>
                        </div>

                        <!-- Mobile Card View -->
                        <div v-else class="mobile-view">
                            <div class="mobile-affiliates-container" ref="affiliatesContainer">
                                <v-card 
                                    v-for="order in allAffiliateOrders" 
                                    :key="order.id" 
                                    class="mb-3 affiliate-card"
                                    elevation="2">
                                    <v-card-text>
                                        <div class="d-flex justify-space-between align-center mb-2">
                                            <div class="order-id font-weight-bold">Đơn #{{ order.id }}</div>
                                            <v-chip :color="getStatusColor(order.status)" size="small">
                                                {{ getStatusText(order.status) }}
                                            </v-chip>
                                        </div>
                                        
                                        <div class="affiliate-info">
                                            <div class="info-row">
                                                <v-icon size="16" color="primary" class="mr-2">mdi-package</v-icon>
                                                <strong>Sản phẩm:</strong>
                                                <div class="ml-2">
                                                    <div v-if="order.product_names && order.product_names.length > 0">
                                                        {{ order.product_names.join(', ') }}
                                                    </div>
                                                    <div v-else-if="order.product_name">
                                                        {{ order.product_name }}
                                                    </div>
                                                    <div v-else class="text-grey">
                                                        Chưa có sản phẩm
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div class="info-row">
                                                <v-icon size="16" color="success" class="mr-2">mdi-currency-usd</v-icon>
                                                <strong>Giá bán:</strong>
                                                <span class="ml-2 font-weight-bold text-success">{{ formatCurrency(order.price) }}</span>
                                            </div>
                                            
                                            <div class="info-row">
                                                <v-icon size="16" color="info" class="mr-2">mdi-percent</v-icon>
                                                <strong>Chia sẻ:</strong>
                                                <v-chip color="info" size="x-small" class="ml-2">
                                                    {{ order.shared }}%
                                                </v-chip>
                                            </div>
                                            
                                            <div class="info-row">
                                                <v-icon size="16" color="warning" class="mr-2">mdi-cash</v-icon>
                                                <strong>Lợi nhuận:</strong>
                                                <span class="ml-2 font-weight-bold text-warning">{{ formatCurrency(order.profit) }}</span>
                                            </div>
                                            
                                            <div class="info-row">
                                                <v-icon size="16" color="purple" class="mr-2">mdi-account-group</v-icon>
                                                <strong>Cộng tác viên:</strong>
                                                <div class="ml-2">
                                                    <div class="seller-row">
                                                        <v-icon size="12" class="mr-1">mdi-account</v-icon>
                                                        <span class="text-caption">{{ order.seller_name }}</span>
                                                    </div>
                                                    <div class="seller-row">
                                                        <v-icon size="12" class="mr-1">mdi-email</v-icon>
                                                        <span class="text-caption text-grey-darken-1">{{ order.seller_email }}</span>
                                                    </div>
                                                    <div class="seller-row">
                                                        <v-icon size="12" class="mr-1">mdi-phone</v-icon>
                                                        <span class="text-caption text-grey-darken-1">{{ order.seller_phone }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div class="info-row">
                                                <v-icon size="16" color="grey" class="mr-2">mdi-calendar</v-icon>
                                                <strong>Ngày tạo:</strong>
                                                <span class="ml-2">{{ formatDate(order.created_at) }}</span>
                                            </div>
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </div>
                            
                            <!-- Loading indicator for infinite scroll -->
                            <div v-if="loadingMore" class="text-center py-4">
                                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                <div class="mt-2">Đang tải thêm...</div>
                            </div>
                            
                            <!-- End of list indicator -->
                            <div v-if="hasReachedEnd && allAffiliateOrders.length > 0" class="text-center py-4 text-grey">
                                <v-icon>mdi-check-circle</v-icon>
                                <div>Đã hiển thị tất cả đơn hàng</div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
            {{ snackbarText }}
        </v-snackbar>
    </div>
</template>

<script>
import axios from '@/http';

export default {
    name: 'Affiliate',
    data() {
        return {
            // Affiliate Orders Table
            affiliateOrders: [],
            allAffiliateOrders: [], // For infinite scroll
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 1,
            search: '',
            sortBy: 'created_at',
            sortOrder: 'DESC',
            loadingMore: false, // For infinite scroll loading
            hasReachedEnd: false, // Track if we've loaded all data
            currentMobilePage: 1, // Separate page tracking for mobile
            
            // Statistics
            totalProfit: 0,
            topAffiliates: [],
            
            // UI States
            pageLoading: false,
            snackbar: false,
            snackbarText: '',
            snackbarColor: 'success',
        }
    },
    methods: {
        async fetchAffiliateOrders(page = this.page, limit = this.limit, search = this.search, sortBy = this.sortBy, sortOrder = this.sortOrder, showPageLoading = false, append = false) {
            if (showPageLoading) {
                this.pageLoading = true;
            }
            if (append) {
                this.loadingMore = true;
            }
            
            try {
                const res = await axios.get('/affiliate/orders', {
                    params: { page, limit, search, sort_by: sortBy, sort_order: sortOrder }
                });
                
                if (append) {
                    // For mobile infinite scroll
                    this.allAffiliateOrders = [...this.allAffiliateOrders, ...res.data.data];
                    this.hasReachedEnd = res.data.data.length < limit || page >= res.data.totalPages;
                } else {
                    // For desktop pagination or initial load
                    this.affiliateOrders = res.data.data;
                    this.allAffiliateOrders = res.data.data;
                }
                
                this.total = res.data.total;
                this.page = res.data.page;
                this.totalPages = res.data.totalPages;
            } catch (e) {
                this.snackbarText = 'Lấy danh sách đơn hàng affiliate thất bại!';
                this.snackbarColor = 'error';
                this.snackbar = true;
            } finally {
                if (showPageLoading) {
                    this.pageLoading = false;
                }
                if (append) {
                    this.loadingMore = false;
                }
            }
        },

        async loadMoreAffiliateOrders() {
            if (this.loadingMore || this.hasReachedEnd) return;
            
            this.currentMobilePage++;
            await this.fetchAffiliateOrders(
                this.currentMobilePage, 
                this.limit, 
                this.search, 
                this.sortBy, 
                this.sortOrder, 
                false, 
                true // append = true
            );
        },

        resetMobileData() {
            this.allAffiliateOrders = [];
            this.currentMobilePage = 1;
            this.hasReachedEnd = false;
        },
        
        async fetchTotalProfit() {
            try {
                const res = await axios.get('/affiliate/total-profit');
                this.totalProfit = res.data.total_profit || 0;
            } catch (e) {
                console.error('Error fetching total profit:', e);
            }
        },
        
        async fetchTopAffiliates() {
            try {
                const res = await axios.get('/affiliate/top-affiliates', {
                    params: { limit: 5 }
                });
                this.topAffiliates = res.data.data || [];
            } catch (e) {
                console.error('Error fetching top affiliates:', e);
            }
        },
        
        sort(column) {
            if (this.sortBy === column) {
                this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
            } else {
                this.sortBy = column;
                this.sortOrder = 'ASC';
            }
            this.page = 1;
            this.resetMobileData();
            this.fetchAffiliateOrders(1, this.limit, this.search, this.sortBy, this.sortOrder);
        },
        
        onSearchInput() {
            clearTimeout(this._searchTimeout);
            this._searchTimeout = setTimeout(() => {
                this.page = 1;
                this.resetMobileData();
                this.fetchAffiliateOrders(1, this.limit, this.search, this.sortBy, this.sortOrder);
            }, 400);
        },
        
        onPageChange(newPage) {
            this.page = newPage;
            this.fetchAffiliateOrders(newPage, this.limit, this.search, this.sortBy, this.sortOrder);
        },
        
        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleString('vi-VN');
        },
        
        formatCurrency(amount) {
            if (!amount) return '0 ₫';
            return new Intl.NumberFormat('vi-VN', {
                style: 'currency',
                currency: 'VND'
            }).format(amount);
        },
        
        getRankColor(index) {
            const colors = ['#FFD700', '#C0C0C0', '#CD7F32', '#4CAF50', '#2196F3'];
            return colors[index] || '#9E9E9E';
        },
        
        getStatusColor(status) {
            const statusColors = {
                0: 'warning',
                1: 'success'
            };
            return statusColors[status] || 'grey';
        },
        
        getStatusText(status) {
            const statusTexts = {
                0: 'Chờ thanh toán',
                1: 'Đã thanh toán',
            };
            return statusTexts[status] || 'Không xác định';
        },

        setupInfiniteScroll() {
            if (!('IntersectionObserver' in window)) return;
            
            this.$nextTick(() => {
                if (this.$refs.affiliatesContainer) {
                    this.observer = new IntersectionObserver((entries) => {
                        const target = entries[0];
                        if (target.isIntersecting && this.$vuetify.display.mobile) {
                            this.loadMoreAffiliateOrders();
                        }
                    }, {
                        root: null,
                        rootMargin: '200px',
                        threshold: 0.1
                    });
                    
                    // Create a sentinel element at the bottom
                    const sentinel = document.createElement('div');
                    sentinel.style.height = '1px';
                    sentinel.style.background = 'transparent';
                    this.$refs.affiliatesContainer?.appendChild(sentinel);
                    this.observer.observe(sentinel);
                }
            });
        }
    },
    
    async mounted() {
        this.pageLoading = true;
        try {
            await Promise.all([
                this.fetchAffiliateOrders(this.page, this.limit, this.search, this.sortBy, this.sortOrder),
                this.fetchTotalProfit(),
                this.fetchTopAffiliates()
            ]);
        } finally {
            this.pageLoading = false;
        }
        this.setupInfiniteScroll();
    },

    beforeUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}
</script>

<style scoped>
.search-input {
    max-width: 320px;
    margin-bottom: 0;
}

.custom-pagination {
    margin-top: 20px;
}

.v-list-item-content {
    padding: 8px 0;
}

.affiliate-content .v-list-item-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 2px;
}

.affiliate-content .v-list-item-subtitle {
    font-size: 12px;
    line-height: 1.4;
    margin-bottom: 2px;
}

.text-success {
    color: #4CAF50 !important;
}

.text-info {
    color: #2196F3 !important;
}

.font-weight-bold {
    font-weight: 600 !important;
}

.font-weight-medium {
    font-weight: 500 !important;
}

.text-grey {
    color: #999;
    font-style: italic;
}

.seller-info {
    padding: 4px 0;
}

.seller-row {
    display: flex;
    align-items: center;
    margin-bottom: 2px;
}

.seller-row:last-child {
    margin-bottom: 0;
}

.seller-row .v-icon {
    opacity: 0.7;
}

.seller-row .text-caption {
    line-height: 1.2;
}

.table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.table-container .v-table {
    min-width: 1000px;
}

.affiliate-item {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 12px 0;
}

.affiliate-item:last-child {
    border-bottom: none;
}

.affiliate-content {
    text-align: left;
}

.affiliate-name {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}

.affiliate-email {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}

.affiliate-profit {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 700;
}

.affiliate-orders {
    display: flex;
    align-items: center;
}

.affiliate-name .v-icon {
    color: #1976D2;
    opacity: 0.8;
}

.affiliate-email .v-icon {
    color: #757575;
    opacity: 0.8;
}

.affiliate-profit .v-icon {
    color: #4CAF50;
    opacity: 0.8;
}

.affiliate-orders .v-icon {
    color: #2196F3;
    opacity: 0.8;
}

.affiliate-item .v-avatar {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.1);
}

.affiliate-item .v-avatar span {
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.top-affiliate-card .v-card-title {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    color: #1565C0;
    border-radius: 4px 4px 0 0;
}

.top-affiliate-card .v-card-subtitle {
    color: #757575;
    font-style: italic;
}

.affiliate-item:hover {
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
    transition: all 0.3s ease;
}

.affiliate-item:hover .v-avatar {
    transform: scale(1.05);
    transition: transform 0.3s ease;
}

/* Mobile Card Styles */
.mobile-view {
  padding: 0;
}

.mobile-affiliates-container {
  padding: 8px 0;
}

.affiliate-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.affiliate-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.affiliate-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-height: 24px;
  font-size: 14px;
}

.info-row strong {
  min-width: 70px;
  font-weight: 600;
}

.info-row .ml-2 {
  flex: 1;
  word-break: break-word;
}

.order-id {
  word-break: break-word;
  line-height: 1.3;
}

/* Desktop responsive adjustments */
@media (max-width: 1200px) {
  .desktop-view .v-table thead th {
    font-size: 13px;
    padding: 8px 4px;
  }
  
  .desktop-view .v-table tbody td {
    font-size: 13px;
    padding: 8px 4px;
  }

  .table-container {
    overflow-x: auto;
  }
}

/* Mobile specific styles */
@media (max-width: 768px) {
  .search-input {
    max-width: 100% !important;
    margin-bottom: 16px;
  }
  
  .v-card-title {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 16px;
  }
  
  .mobile-affiliates-container {
    padding: 4px 0;
  }
  
  .affiliate-card {
    margin-bottom: 12px !important;
  }
  
  .info-row {
    font-size: 13px;
  }
  
  .info-row strong {
    min-width: 65px;
    font-size: 13px;
  }
  
  .order-id {
    font-size: 15px;
  }

  /* Responsive columns for mobile */
  .v-row {
    margin: 0 !important;
  }
  
  .v-col {
    padding: 8px !important;
  }
  
  /* Stack columns on mobile */
  .v-col[cols="12"][md="4"],
  .v-col[cols="12"][md="8"] {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }
}
</style>